/**
 * « Préparer l'année » — les ENTRETIENS d'après les gammes (2026-10-30) : ce que l'écran en dit.
 *
 * C'est l'utilisateur qui choisit les machines (« je coche les matériels que je veux mettre dans la visite », Benjamin) : les
 * propositions sont rangées par site, une case par ligne, « tout cocher » par site. RIEN n'est coché au chargement (Benjamin,
 * 2026-10-31 : cocher d'office les compresseurs et sécheurs de TOUS les sites faisait créer des interventions partout d'un clic).
 *
 * Aucune règle de calcul ici : le serveur dit le palier, la date, l'état et la raison.
 */
import type { PropositionEntretienAnnee } from '../api/client';
import { jourFr } from './planning';
import { heures } from './prochain-entretien';
import { phraseRetard, type FiltreAnnee } from './preparer-annee';

const sansAccents = (t: string): string =>
  t
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/** Le nom d'une machine, comme on la reconnaît. */
export function nomMachine(l: PropositionEntretienAnnee): string {
  return l.machine.libelle ?? l.machine.modele ?? 'Machine';
}

/** Filtre par état et par recherche (client, site, machine, famille, gamme : tous les mots, sans accents). */
export function filtrerEntretiens(
  lignes: readonly PropositionEntretienAnnee[],
  filtre: FiltreAnnee,
  recherche: string,
): readonly PropositionEntretienAnnee[] {
  const mots = sansAccents(recherche)
    .split(/\s+/)
    .filter((m) => m !== '');
  return lignes.filter((l) => {
    if (filtre !== 'TOUTES' && l.etat !== filtre) return false;
    if (mots.length === 0) return true;
    const texte = sansAccents(
      [l.client, l.site?.nom, l.site?.ville, l.machine.libelle, l.machine.modele, l.machine.numeroSerie, l.machine.designationClient, l.machine.famille, l.gamme?.label]
        .filter(Boolean)
        .join(' '),
    );
    return mots.every((m) => texte.includes(m));
  });
}

export interface GroupeSite {
  readonly cle: string;
  readonly libelle: string;
  readonly lignes: readonly PropositionEntretienAnnee[];
}

/** Une machine et toutes ses visites de l'horizon : UNE ligne par machine à l'écran (Benjamin, 2026-10-31). */
export interface GroupeMachine {
  readonly id: string;
  readonly nom: string;
  readonly famille: string;
  readonly sansGamme: boolean;
  readonly lignes: readonly PropositionEntretienAnnee[];
}

/** Rang d'une famille : compresseurs, puis sécheurs, puis le reste (filtres, purgeurs, séparateurs, réservoirs…). */
function rangFamille(famille: string): number {
  const f = sansAccents(famille);
  if (f.includes('compresseur')) return 0;
  if (f.includes('secheur')) return 1;
  return 2;
}

/** Les visites d'un site regroupées par machine, rangées par famille puis par nom ; dans une machine, par date. */
export function groupesParMachine(lignes: readonly PropositionEntretienAnnee[]): readonly GroupeMachine[] {
  const parId = new Map<string, PropositionEntretienAnnee[]>();
  for (const l of lignes) {
    const liste = parId.get(l.machine.id) ?? [];
    liste.push(l);
    parId.set(l.machine.id, liste);
  }
  return [...parId.entries()]
    .map(([id, ls]) => {
      const premiere = ls[0] as PropositionEntretienAnnee;
      return {
        id,
        nom: nomMachine(premiere),
        famille: premiere.machine.famille,
        sansGamme: premiere.gamme === null,
        lignes: [...ls].sort((a, b) => (a.date ?? '9999').localeCompare(b.date ?? '9999')),
      };
    })
    .sort((a, b) => rangFamille(a.famille) - rangFamille(b.famille) || a.famille.localeCompare(b.famille, 'fr') || a.nom.localeCompare(b.nom, 'fr'));
}

/** Les visites d'une machine qu'on peut cocher (à créer). */
export function cochablesMachine(m: GroupeMachine): readonly string[] {
  return m.lignes.filter((l) => l.etat === 'A_CREER').map((l) => l.id);
}

/** La case d'une machine : coche toutes ses visites à créer ; si toutes le sont déjà, les décoche. */
export function basculerMachine(cochees: ReadonlySet<string>, m: GroupeMachine): ReadonlySet<string> {
  const ids = cochablesMachine(m);
  const suite = new Set(cochees);
  const tout = ids.length > 0 && ids.every((id) => suite.has(id));
  for (const id of ids) {
    if (tout) suite.delete(id);
    else suite.add(id);
  }
  return suite;
}

/** État de la case d'une machine : toutes, aucune ou une partie de ses visites cochées. */
export function etatCaseMachine(cochees: ReadonlySet<string>, m: GroupeMachine): 'TOUTES' | 'AUCUNE' | 'PARTIEL' {
  const ids = cochablesMachine(m);
  const n = ids.filter((id) => cochees.has(id)).length;
  if (n === 0) return 'AUCUNE';
  return n === ids.length ? 'TOUTES' : 'PARTIEL';
}

/** Les lignes rangées par site (client puis site), et dans un site par machine puis par date (sans date en dernier). */
export function groupesParSite(lignes: readonly PropositionEntretienAnnee[]): readonly GroupeSite[] {
  const groupes = new Map<string, { libelle: string; lignes: PropositionEntretienAnnee[] }>();
  for (const l of lignes) {
    const cle = l.site?.id ?? 'sans-site';
    const libelle = l.site ? [l.client, `${l.site.nom}${l.site.ville ? ` (${l.site.ville})` : ''}`].filter(Boolean).join(' · ') : 'Machines sans site';
    const g = groupes.get(cle) ?? { libelle, lignes: [] };
    g.lignes.push(l);
    groupes.set(cle, g);
  }
  const ordre = (a: PropositionEntretienAnnee, b: PropositionEntretienAnnee) =>
    nomMachine(a).localeCompare(nomMachine(b), 'fr') || (a.date ?? '9999').localeCompare(b.date ?? '9999');
  return [...groupes.entries()]
    .map(([cle, g]) => ({ cle, libelle: g.libelle, lignes: [...g.lignes].sort(ordre) }))
    .sort((a, b) => (a.cle === 'sans-site' ? 1 : b.cle === 'sans-site' ? -1 : a.libelle.localeCompare(b.libelle, 'fr')));
}

/** Les lignes d'un groupe qu'on peut cocher (à créer). */
export function cochables(g: GroupeSite): readonly string[] {
  return g.lignes.filter((l) => l.etat === 'A_CREER').map((l) => l.id);
}

/** « Tout cocher » d'un site : coche tout ce qui est à créer ; si tout l'est déjà, décoche tout le site. */
export function basculerSite(cochees: ReadonlySet<string>, g: GroupeSite): ReadonlySet<string> {
  const ids = cochables(g);
  const suite = new Set(cochees);
  const tout = ids.length > 0 && ids.every((id) => suite.has(id));
  for (const id of ids) {
    if (tout) suite.delete(id);
    else suite.add(id);
  }
  return suite;
}

/** Le palier, en clair : « D (révision) », « A ». */
export function phrasePalier(l: PropositionEntretienAnnee): string {
  if (l.genre === 'SANS_GAMME') return 'Pas de gamme';
  if (!l.palier) return 'À choisir';
  return l.palier.libelle ? `${l.palier.code} · ${l.palier.libelle}` : l.palier.code;
}

/** Quand : « en retard (point de 69 000 h) », « vers le 27/11/2026 · 72 000 h », « le 15/09/2027 », « premier entretien, dans l'année ». */
export function phraseQuand(l: PropositionEntretienAnnee): string {
  const h = (n: number | null) => (n === null ? '' : heures(n));
  switch (l.genre) {
    case 'RETARD':
      return `En retard (point de ${h(l.cible)})`;
    case 'POINT':
      return l.date ? `Vers le ${jourFr(l.date)} · ${h(l.cible)}` : `${h(l.cible)} · date à estimer`;
    case 'ECHEANCE':
      return l.date ? `Le ${jourFr(l.date)}` : '—';
    case 'PREMIER':
      return 'Premier entretien, dans l’horizon';
    case 'SANS_GAMME':
      return 'Visite sans palier, dans l’horizon';
    default:
      return '—';
  }
}

/** Une date ESTIMÉE dépassée (point au compteur) : ce sont les heures qui comptent, la date n'est qu'une estimation. */
export const DATE_ESTIMEE_DEPASSEE = 'Date estimée dépassée : relève le compteur pour savoir où en est la machine.';

/**
 * Le complément de « quand », ou rien. Pour un point au compteur, la date n'est qu'une estimation (Benjamin : les heures
 * déclenchent) : une date passée ne fait pas un retard, elle dit de relever le compteur. Une échéance au calendrier, elle, est
 * une vraie date : « en retard de N jours ».
 */
export function phraseRetardEntretien(l: { readonly genre: string | null; readonly retardJours: number | null }): string | null {
  if (l.genre === 'RETARD' || l.retardJours === null || l.retardJours <= 0) return null;
  if (l.genre === 'POINT') return DATE_ESTIMEE_DEPASSEE;
  return phraseRetard(l.retardJours);
}

/** Ce que la création envoie : les échéances ET les entretiens cochés (des identifiants, rien d'autre). */
export function corpsCreationAnnee(
  natureId: string,
  echeances: ReadonlySet<string>,
  entretiens: ReadonlySet<string>,
): { natureId: string; obligationIds: string[]; entretienIds: string[] } {
  return { natureId, obligationIds: [...echeances], entretienIds: [...entretiens] };
}
